import { COMMIT_STAGE } from '../components/BulkLotNdcCommitProgress';

/**
 * POM-6043 — how far the merged PDF has got, which is a different question from whether the batch
 * has settled.
 *
 * settle() reports the printable id set; turning that into a PDF is a second round trip this
 * dialog does not own. Conflating the two is what let the bar announce ready labels while the
 * PREVIOUS file was still the one behind Download.
 */
export const LABEL_BUILD = {
  BUILDING: 'BUILDING',
  READY: 'READY',
  FAILED: 'FAILED',
  /**
   * Settled on an empty printable set, so no build was ever asked for.
   *
   * Distinct from FAILED because the two need opposite sentences: FAILED means the PDF was
   * attempted and Regenerate Label is the way back, while this means there is nothing to build and
   * a rebuild would produce nothing either.
   */
  NOTHING_TO_PRINT: 'NOTHING_TO_PRINT',
};

/**
 * POM-6010 — the one line under the review's print bar that says why the buttons are as they are.
 *
 * Pulled out of BulkLotNdcReviewDialog so it can be read and tested on its own: it is the only
 * place that explains a locked Print button, and every branch below corresponds to a different
 * reason the batch is not ready. Getting one wrong leaves the user staring at a disabled button
 * with no account of what to do about it.
 *
 * Ordered by precedence, not by likelihood — an in-flight commit outranks everything, and the
 * "ready" line is only reachable once nothing else applies.
 */
const bulkLotNdcHelperText = ({
  commitStage,
  counts,
  settled,
  excludeBlocked,
  labelBuild = LABEL_BUILD.READY,
  blockUnusableLotItems = false,
}) => {
  if (commitStage === COMMIT_STAGE.COMMITTING) return 'Saving — this takes a moment.';

  if (commitStage === COMMIT_STAGE.REGENERATING) {
    return 'Rebuilding the labels — actions unlock when they are ready.';
  }

  if (counts.changed > 0) {
    return `Commit ${
      counts.changed === 1 ? 'the change' : 'the changes'
    } to rebuild the labels, then print.`;
  }

  // Above FAILED: an empty batch never reached a build, so naming Regenerate Label as the way back
  // would send the user at a control that cannot help — a rebuild of nothing is still nothing. Says
  // the two things that do move this batch forward instead.
  if (labelBuild === LABEL_BUILD.NOTHING_TO_PRINT) {
    // POM-6121: under the switch the opt-out is ticked and disabled, so naming it sends the user
    // at the one control on this screen they cannot touch. Fixing the rows is the only way out.
    return blockUnusableLotItems
      ? 'Nothing to print — a label needs a lot that can be dispensed. Fix the rows above, or ' +
          'close and deal with them in the queue.'
      : 'Nothing to print — pick another lot above, or untick the box to print these items anyway.';
  }

  // Below the uncommitted-changes line on purpose: committing rebuilds the labels anyway, so
  // "commit, then print" is the more useful instruction even when the last build failed.
  if (labelBuild === LABEL_BUILD.FAILED) {
    return 'The labels could not be built — use Regenerate Label to try again.';
  }

  if (!settled || labelBuild === LABEL_BUILD.BUILDING) return 'Building the labels…';

  if (counts.blocked > 0 && excludeBlocked) {
    return `Printing without ${counts.blocked > 1 ? 'them' : 'it'} — ${
      counts.blocked > 1 ? 'they stay' : 'it stays'
    } in the queue.`;
  }

  // Reachable only once the user has unticked the box, so the risk is what needs saying: these
  // labels print with a lot that may already be wrong. Not a lock, just the sentence the user
  // needs before pressing Print. Unreachable under the switch, which forces excludeBlocked above.
  if (counts.blocked > 0) {
    return (
      `${counts.blocked} label${counts.blocked > 1 ? 's' : ''} will print with a lot that may ` +
      `not match what is dispensed — fix ${counts.blocked > 1 ? 'them' : 'it'} above, or tick ` +
      'the box to leave them out.'
    );
  }

  return 'Labels are ready — download or print as before.';
};

/**
 * Why Apply is disabled, in one sentence.
 *
 * Here rather than inline in the dialog because every branch is a sentence the operator acts on,
 * and because the dialog is at the file-length cap. A pointer, never the manual panel's own error
 * repeated — that alert is on screen whenever it applies, and the same sentence twice reads as
 * two problems.
 */
export const applyDisabledReason = ({ manualMode, isNoChange, hasNdcSelected, hasLotsOnOffer }) => {
  if (manualMode) return 'Finish the lot details above to apply.';
  if (isNoChange) return 'This is the NDC and lot already on this item — nothing to change.';

  return hasNdcSelected && hasLotsOnOffer
    ? 'Select a lot to apply.'
    : 'Select an NDC with usable stock.';
};

export default bulkLotNdcHelperText;
